import * as React from "react";
import { useHistory } from "react-router-dom";
import { AvatarList, SimplifiedUser } from "../AvatarList";
import { assertNever } from "../../util";
import { SmallButton } from "../SmallButton";
import { TestIds } from "../../testIds";
import {
  EnvelopeIcon,
  UserCircleIcon,
  ArrowUturnLeftIcon,
  ChatBubbleLeftEllipsisIcon,
  PlusCircleIcon,
  HeartIcon,
  MusicalNoteIcon,
  TagIcon,
} from "@heroicons/react/24/solid";

type IconTypes =
  | "envelope"
  | "user"
  | "new"
  | "comment"
  | "music"
  | "tag"
  | "heart"
  | "reply"

  // unused
  | "comment-heart"; // was for likes on comments

const MakeIcon = (
  icon: IconTypes,
  background?: boolean,
  compact?: boolean
): JSX.Element | undefined => {
  const baseClass = background
    ? compact
      ? "h-12 w-12"
      : "h-24 w-24"
    : "h-5 w-5";

  const bgColor = "text-slate-100 dark:text-slate-950/20";
  const colors: Record<IconTypes, { normal: string; bg: string }> = {
    envelope: { normal: "text-amber-500", bg: bgColor },
    user: { normal: "text-violet-500", bg: bgColor },
    reply: { normal: "text-amber-500", bg: bgColor },
    comment: { normal: "text-blue-500", bg: bgColor },
    new: { normal: "text-emerald-500", bg: bgColor },
    "comment-heart": { normal: "text-pink-400", bg: bgColor },
    music: { normal: "text-emerald-500", bg: bgColor },
    heart: { normal: "text-rose-500", bg: bgColor },
    tag: { normal: "text-teal-500", bg: bgColor },
  };

  const colorClass = background ? colors[icon].bg : colors[icon].normal;
  const className = `${baseClass} ${colorClass}`;

  switch (icon) {
    case "envelope":
      return <EnvelopeIcon className={className} />;
    case "user":
      return <UserCircleIcon className={className} />;
    case "reply":
      return <ArrowUturnLeftIcon className={className} />;
    case "comment":
      return <ChatBubbleLeftEllipsisIcon className={className} />;
    case "new":
      return <PlusCircleIcon className={className} />;
    case "comment-heart":
      return <HeartIcon className={className} />;
    case "music":
      return <MusicalNoteIcon className={className} />;
    case "heart":
      return <HeartIcon className={className} />;
    case "tag":
      return <TagIcon className={className} />;
    default:
      assertNever(icon);
      return undefined;
  }
};

type FeedElementContainerProps = {
  title: JSX.Element;

  avatars?: SimplifiedUser[];
  icon?: IconTypes;
  subtitle?: JSX.Element;
  bottom?: JSX.Element;
  compact?: boolean;
  href?: string;
};

/**
 * This is a generic feed element. It's anything user-generated you see in the
 * main section of the feed.
 */
export const FeedElementContainer = (
  props: React.PropsWithChildren<FeedElementContainerProps>
): JSX.Element => {
  const { avatars, icon, title, subtitle, bottom, children, compact, href } =
    props;
  const history = useHistory();
  const backgroundIcon = icon ? MakeIcon(icon, true, compact) : undefined;

  const hoverClass = href
    ? "cursor-pointer transition-all hover:border-slate-300 hover:shadow-md dark:hover:border-white/20"
    : "";

  const handleClick = (e: React.MouseEvent) => {
    if (!href) return;
    // Don't navigate if clicking on a link or button
    const target = e.target as HTMLElement;
    if (target.closest("a") || target.closest("button")) return;
    history.push(href);
  };

  if (compact) {
    return (
      <div
        className={`relative mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-800 ${hoverClass}`}
        onClick={handleClick}
      >
        {backgroundIcon && (
          <div className="z-0 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
            {backgroundIcon}
          </div>
        )}
        <div className="relative z-10 flex items-center gap-3 px-3 py-2">
          {avatars && (
            <div className="flex-shrink-0">
              <AvatarList users={avatars} compact />
            </div>
          )}
          <div
            className="feed-title min-w-0 flex-1 text-base font-semibold text-slate-800 dark:text-slate-100"
            data-testid={TestIds.FeedTitle}
          >
            {title}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-800 ${hoverClass}`}
      onClick={handleClick}
      data-testid={TestIds.FeedCard}
    >
      {backgroundIcon && (
        <div className="z-0 pointer-events-none absolute right-4 top-4">
          {backgroundIcon}
        </div>
      )}
      <div
        className={`relative z-10 flex gap-4 p-4 ${children || bottom ? "pb-0" : ""}`}
      >
        {avatars && (
          <div className="flex-shrink-0">
            <AvatarList users={avatars} />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div
            className="feed-title text-lg font-semibold text-slate-800 dark:text-slate-100"
            data-testid={TestIds.FeedTitle}
          >
            {title}
          </div>
          {subtitle && (
            <div className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {children && (
        <div className="px-4 py-3 text-slate-700 dark:text-slate-300">
          {children}
        </div>
      )}

      {bottom && (
        <div className="border-t border-slate-200 bg-slate-100 px-4 py-2.5 dark:border-white/10 dark:bg-slate-700">
          {bottom}
        </div>
      )}
    </div>
  );
};

type FeedPanelHeadingProps = {
  title: JSX.Element;
  children?: React.ReactNode;
};

/**
 * This is a generic feed panel. It's probably anything you see on the right
 * side of the feed.
 */
export const FeedPanel = ({
  title,
  children,
}: FeedPanelHeadingProps): JSX.Element => (
  <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-800">
    <h2 className="mb-3 text-xl font-semibold text-slate-800 dark:text-white">
      {title}
    </h2>
    <div className="text-slate-600 dark:text-slate-300">{children}</div>
  </div>
);

type ActionPanelProps = {
  like?: {
    count: number;
    url?: string;
    hasLiked?: boolean;
  };

  reply?: {
    url: string;
  };

  seePost?: {
    url: string;
  };

  delete?: {
    url: string;
    deleted: boolean;
  };

  date?: {
    date: string;
  };
};

/**
 * This is the panel on the bottom of feed items and comments that
 * allows you to take actions like commenting and liking.
 */
export const ActionPanel = (props: ActionPanelProps): JSX.Element => {
  const elements: JSX.Element[] = [];

  if (props.like) {
    elements.push(
      <SmallButton
        key="like"
        to={props.like.url}
        badge={props.like.count}
        icon={<HeartIcon className="h-4 w-4 text-rose-500" />}
      >
        {props.like.hasLiked ? "Liked" : "Like"}
      </SmallButton>
    );
  }

  if (props.reply) {
    elements.push(
      <SmallButton
        key="reply"
        to={props.reply.url}
        icon={<ArrowUturnLeftIcon className="h-4 w-4" />}
      >
        Reply
      </SmallButton>
    );
  }

  if (props.seePost) {
    elements.push(
      <SmallButton
        key="seePost"
        to={props.seePost.url}
        icon={<ArrowUturnLeftIcon className="h-4 w-4" />}
      >
        See Post
      </SmallButton>
    );
  }

  if (elements.length === 0) {
    return <div></div>;
  }

  return <div className="flex items-center gap-2">{elements}</div>;
};
